import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, User, X } from 'lucide-react';

import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { payerApi } from '../lib/api/payerApi';
import { PayerSummary } from '../lib/api/types';

interface PayerSelectorProps {
  selectedPayerId?: string;
  onPayerSelect: (payer: PayerSummary | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

export const PayerSelector: React.FC<PayerSelectorProps> = ({
  selectedPayerId,
  onPayerSelect,
  label = 'Payer',
  placeholder = 'Select a payer',
  required = false,
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch payers
  const {
    data: payersData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['payers'],
    queryFn: () => payerApi.getAllPayers(),
  });

  const payers = payersData?.Payers || [];
  const selectedPayer = payers.find(payer => payer.PayerAccountId === selectedPayerId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePayerSelect = (payer: PayerSummary) => {
    onPayerSelect(payer);
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    onPayerSelect(null);
    setIsOpen(false);
  };

  if (queryError) {
    return (
      <div className="space-y-2">
        {label && (
          <label className="text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="text-sm text-red-600">
          Error loading payers: {queryError instanceof Error ? queryError.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" ref={dropdownRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={`w-full justify-between ${error ? 'border-red-500' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {isLoading ? (
              'Loading payers...'
            ) : selectedPayer ? (
              <span>
                {selectedPayer.Name} ({selectedPayer.EmailAddress || selectedPayer.PayerAccountId})
              </span>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>

        {isOpen && (
          <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto">
            <CardContent className="p-2">
              {payers.length === 0 ? (
                <div className="text-sm text-gray-500 p-2">
                  No payers available
                </div>
              ) : (
                <>
                  {selectedPayer && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleClearSelection}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear selection
                    </Button>
                  )}
                  {payers.map((payer) => (
                    <Button
                      key={payer.PayerAccountId}
                      type="button"
                      variant="ghost"
                      className={`w-full justify-start ${
                        selectedPayerId === payer.PayerAccountId
                          ? 'bg-blue-50 text-blue-700'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handlePayerSelect(payer)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      <div className="text-left">
                        <div className="font-medium">{payer.Name}</div>
                        <div className="text-sm text-gray-500">
                          {payer.EmailAddress || payer.PayerAccountId}
                        </div>
                      </div>
                    </Button>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};
